import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { isArray, isEmpty, map } from 'lodash/fp';
import Downshift from 'downshift';
import { TextField, MenuList, MenuItem } from '@material-ui/core';
import { Search } from '@material-ui/icons';

const S = {
  // TODO: :hover 스타일 적용 및 highlighted 시 border 너비 2px로 변경
  // :hover {
  //   border-color: ${({ theme }) => theme.palette.primary.main};
  // }
  Boundary: styled.div`
    border: 1px solid ${({ theme }) => theme.palette.grey[400]};
    border-radius: 4px;
    ${({ highlighted, theme }) => highlighted && css`
      border: 1px solid ${theme.palette.primary.main};
    `};
  `,
  Wrapper: styled.div`
    position: relative;
    ${({ highlighted, theme }) => highlighted && css`
      ::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: ${theme.palette.primary.main};
      }
    `}
  `,
  Search: styled(Search)`
    position: absolute;
    top: 50%;
    left: 8px;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  TextField: styled(TextField)`
    width: 100%;
    .MuiInput-root::before, .MuiInput-root::after {
      display: none;
    }
    .MuiInput-input {
      box-sizing: border-box;
      width: 100%;
      height: 40px;
      padding-left: 40px;
      color: ${({ theme }) => theme.palette.grey[900]};
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    }
  `,
  FallbackMessage: styled.p`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 224px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    line-height: 1.5;
    text-align: center;
  `,
  MenuList: styled(MenuList)`
    max-height: 224px;
    padding: 0;
    overflow: auto;
    outline: none;
  `,
  MenuItem: styled(MenuItem)`
    align-items: flex-start;
    min-height: 60px;
    padding: 8px 16px 9px;
    :hover {
      background-color: ${({ theme }) => theme.palette.blueGrey[25]};
    }
  `,
};

/**
 * 사용자 입력값과 일치한 목록을 자동으로 완성하여 제안합니다.
 *
 * @version 1.0.0
 * @author [chayeoi](https://github.com/chayeoi)
 */

const Autocomplete = ({
  autoFocus, value, onChange, onInputValueChange, suggestions, className, fallbackMessage, placeholder, type,
}) => {
  const [focused, setFocused] = useState(false);

  return (
    <Downshift
      onChange={onChange}
      onInputValueChange={onInputValueChange}
      itemToString={item => (item ? item.value : '')}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        isOpen,
      }) => {
        const { onBlur, onFocus, ...InputProps } = getInputProps({});

        const handleBlur = () => setFocused(false);
        const handleFocus = () => setFocused(true);

        return (
          <S.Boundary
            className={className}
            highlighted={focused && (suggestions === null || !isOpen)}
            {...getRootProps()}
          >
            <S.Wrapper highlighted={suggestions !== null && isOpen}>
              <S.Search />
              <S.TextField
                type={type}
                placeholder={placeholder}
                value={value}
                InputProps={{
                  onBlur: handleBlur,
                  onFocus: handleFocus,
                  ...InputProps,
                }}
                autoFocus={autoFocus}
              />
            </S.Wrapper>
            {isArray(suggestions) && isEmpty(suggestions) && fallbackMessage
              ? <S.FallbackMessage>{fallbackMessage}</S.FallbackMessage>
              : (
                <S.MenuList {...getMenuProps()}>
                  {isOpen && map(suggestion => (
                    <S.MenuItem
                      {...getItemProps({
                        key: suggestion.id,
                        item: suggestion,
                      })}
                    >
                      {suggestion.content}
                    </S.MenuItem>
                  ), suggestions)}
                </S.MenuList>
              )}
          </S.Boundary>
        );
      }}
    </Downshift>
  );
};

Autocomplete.propTypes = {
  autoFocus: PropTypes.bool,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /** 사용자 입력에 따라 제안될 자동 완성 목록 */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node,
    }),
  ),
  /** 사용자 입력값 */
  value: PropTypes.string.isRequired,
  fallbackMessage: PropTypes.node,
  /** 입력 필드에 값을 입력하기 전에 사용자에게 제공될 힌트 */
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['search', 'type']),
  /**
   * 사용자가 값을 입력할 때 호출될 콜백 함수
   *
   * @param {string} value 사용자 입력값
   */
  onClick: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onInputValueChange: PropTypes.func.isRequired,
};

Autocomplete.defaultProps = {
  autoFocus: false,
  className: undefined,
  fallbackMessage: undefined,
  placeholder: undefined,
  suggestions: null,
  type: 'search',
  onClick: () => {},
};

export default Autocomplete;
